import { HttpClient, HttpContext, HttpHeaders, HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, lastValueFrom } from "rxjs";
import { catchError, filter, map, timeout } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { SKIP_LOADING } from "src/app/shared/interceptors/loading.interceptor";

// AddSectionsFromZip contract (ThreeD returns PascalCase JSON).
export interface SectionMergeReport {
  SectionId: string
  // Name from the zip's catalogs; null when no catalog describes the section.
  SectionName: string | null
  Merged: boolean
  Files: string[]
  Tanks: string[]
  // Distinct region ids referenced by the section's elements, in the zip's spelling (casing/dashes may
  // differ from the regions service ids).
  RegionIds: string[]
  GeometryIn: number
  GeometryOut: number
  PropertyIn: number
  PropertyOut: number
  ReferenceLinesDeduped: number
  StructuralDuplicatesDropped: number
  PropertyDuplicatesDropped: number
  VirtualWeldPairsKept: number
  ReferenceLinesOnly: boolean
}

export interface SectionZipError {
  Entry: string
  Reason: string
  SectionIds: string[]
  Detail: string
}

export interface ReplaceFlowResult {
  Status: 'NotRun' | 'Succeeded' | 'Failed'
  SectionCount: number
  Error: string
  // Elements this phase removed from untouched pieces (the applied removal rows).
  ElementsRemoved?: number
  // Regions whose file of this phase's kind the removals left empty.
  RegionsEmptied?: string[]
  // Regions that had no file of this phase's kind and received their first one.
  RegionsCreated?: number
}

// Why a stored (region, section) piece, or part of it, is planned for removal by a sections-zip upload.
export type RegionRemovalReason = 'IdMoved' | 'IdAmbiguous' | 'ProvenanceDropped'

// Outcome of a removal row.
export type RegionRemovalStatus = 'Planned' | 'Applied' | 'PendingConfirmation' | 'Skipped'

// A region by id and display name.
export interface RemovalDestination {
  Id: string
  Name: string | null
}

// One removal row: the elements of one reason removed from one stored (region, section) piece.
export interface RegionRemoval {
  // The stored region id spelling.
  RegionId: string
  RegionName: string | null
  // The stored section id spelling.
  SectionId: string
  SectionName: string | null
  Reason: RegionRemovalReason
  Status: RegionRemovalStatus
  // The removed element ids as the stored file spells them, in file order (for the Inspection hand-over).
  ElementIds: string[]
  // Where the zip places the elements now; empty for ProvenanceDropped.
  Destinations: RemovalDestination[]
  // Every row of this (region, section) together covers the whole stored piece, as if every row applies.
  PieceBecomesEmpty: boolean
  // Every row on this region together covers the whole stored region file, as if every row applies.
  RegionBecomesEmpty: boolean
  // Geometry elements removed; filled by apply.
  ElementsRemoved: number
}

// A stored (region, section) piece the zip does not touch, with the elements no removal row covers.
export interface UncoveredPiece {
  RegionId: string
  RegionName: string | null
  SectionId: string
  SectionName: string | null
  Count: number
}

// Confirms every IdAmbiguous row of one (region, section); the backend compares the ids normalised.
export interface RemovalConfirmation {
  regionId: string
  sectionId: string
}

// Report of the additive region/section sync from the zip's _AssetInfo.json catalogs (the delta lists are not declared).
export interface CatalogSyncReport {
  // NotRun | Succeeded | Failed | Skipped
  Status: string
  Error: string | null
  CatalogFiles: number
  CatalogRegions: number
  CatalogSections: number
  CatalogLinks: number
  SectionLinksAdded: number
  RegionLinksAdded: number
  DanglingLinksSkipped: number
  TargetsSkipped: number
  RegionsWritten: number
  SectionsWritten: number
}

export interface SectionsZipUploadSummary {
  AssetId: string
  JobId: string
  Success: boolean
  DryRun: boolean
  SectionsFound: number
  GeometryFilesFound: number
  PropertyFilesFound: number
  Sections: SectionMergeReport[]
  SkippedEntries: string[]
  Warnings: string[]
  Errors: SectionZipError[]
  UnknownRegionIds: string[]
  RegionsWithoutBlob: string[]
  UnknownSectionIds: string[]
  GeometryResult: ReplaceFlowResult
  PropertyResult: ReplaceFlowResult
  CatalogSync?: CatalogSyncReport | null
  // Stored pieces the zip leaves untouched whose elements it places on other regions (or whose provenance drops them).
  Removals?: RegionRemoval[]
  // Untouched stored pieces with elements no removal row covers; they stay.
  UncoveredPieces?: UncoveredPiece[]
  // Region files the move scan read.
  RegionsScanned?: number
  // Regions whose geometry file the applied removals emptied.
  RegionsEmptied?: number
  // Region/section links removed after the applied removals.
  LinksRemoved?: number
}

// 202 body of a non-dry-run AddSectionsFromZip: the apply keeps running in the background.
export interface SectionsZipAccepted {
  JobId: string
  Summary: SectionsZipUploadSummary
}

// GET AssetFiles/SectionsZipJobs/{jobId} and GET AssetFiles/SectionsZipJobs?assetId= (404 = no such job).
// Stage is 'catalog' | 'geometry' | 'properties' | 'done'; Current/Total count catalog items or regions.
export interface SectionsZipJobStatus {
  JobId: string
  AssetId: string
  StartedAt: string
  UpdatedAt: string
  FinishedAt: string | null
  Stage: string
  Message: string
  Current: number
  Total: number
  Done: boolean
  Error: string | null
  // Set once the job is done.
  Summary: SectionsZipUploadSummary | null
}

@Injectable({
  providedIn: "root",
})

export class AssetFilesService {

  constructor(private http: HttpClient,) { }

  postProperties(formData: FormData) {
    return this.http.post(
            environment.API_THREED + 'AssetFiles/AddPropertiesToCollectionWithIds', formData, {reportProgress: true, observe: 'events'}
    );
  }

    postGeometries(formData: FormData){
    return this.http.post(
            environment.API_THREED + 'AssetFiles/AddGeometriesToCollectionWithIds', formData , {reportProgress: true, observe: 'events'}
    );
  }

  // Kicks off the backend tank-generation pipeline. Returns immediately with the JobId;
  // progress is reported over SignalR via TankGenerationService.
  generateTankFromScoutSession(req: {
    AssetId: string;
    CollectionId: string;
    ScoutSessionCode: string;
    StrakeCount?: number;
    SectorCount?: number;
    ModelMode?: 'mesh' | 'parametric';
  }): Observable<{ JobId: string; AssetId: string }> {
    return this.http.post<{ JobId: string; AssetId: string }>(
      environment.API_THREED + 'AssetFiles/GenerateTankFromScoutSession',
      req
    );
  }

  // dryRun=true answers 200 with the merge report; dryRun=false answers 202 { JobId, Summary } and the
  // replace runs in the background (progress over SignalR). waitForCompletion is deliberately never sent, and
  // neither is detectMoves (the backend defaults it to true). confirmRemovals carries the confirmed IdAmbiguous
  // pieces as JSON and is omitted when empty.
  uploadSectionsZip(assetId: string, zipFile: File, options: { dryRun: boolean; allowUnknownRegions: boolean; confirmRemovals?: RemovalConfirmation[] }): Observable<SectionsZipUploadSummary | SectionsZipAccepted> {
    const formData = new FormData()
    formData.append('assetId', assetId)
    formData.append('zipFile', zipFile, zipFile.name)
    formData.append('dryRun', options.dryRun ? 'true' : 'false')
    formData.append('allowUnknownRegions', options.allowUnknownRegions ? 'true' : 'false')
    if (options.confirmRemovals?.length) {
      formData.append('confirmRemovals', JSON.stringify(options.confirmRemovals))
    }
    return this.http.post<SectionsZipUploadSummary | SectionsZipAccepted>(
      environment.API_THREED + 'AssetFiles/AddSectionsFromZip', formData
    ).pipe(timeout(1000 * 60 * 60))
  }

  // Status of a sections zip upload job (404 when the job is unknown, e.g. after a service restart).
  // Polled in the background, so kept out of the global loading indicator (SKIP_LOADING).
  getSectionsZipJob(jobId: string): Observable<SectionsZipJobStatus> {
    return this.http.get<SectionsZipJobStatus>(
      environment.API_THREED + 'AssetFiles/SectionsZipJobs/' + encodeURIComponent(jobId),
      { context: new HttpContext().set(SKIP_LOADING, true) }
    )
  }

  // Latest sections zip upload job of the asset (404 when there is none).
  getLatestSectionsZipJob(assetId: string): Observable<SectionsZipJobStatus> {
    return this.http.get<SectionsZipJobStatus>(
      environment.API_THREED + 'AssetFiles/SectionsZipJobs',
      { params: { assetId }, context: new HttpContext().set(SKIP_LOADING, true) }
    )
  }

    postNeutralFile(formData: FormData){
    return this.http.post(
      environment.API_FEA + 'AssetData/UploadData/FemapNeutralFile', formData
    ).pipe(timeout(1000 * 60 * 60));
  }

  postDatFile(formData: FormData) {
    return this.http.post(
      environment.API_FEA + 'AssetData/UploadData/NastranDatFile', formData
    ).pipe(timeout(1000 * 60 * 60));
  }

  postLoadworkRegionGroupReference(formData: FormData) {
    return this.http.post(
      environment.API_FEA + 'AssetData/UploadData/LoadworkRegionGroupReference', formData
    ).pipe(timeout(1000 * 60 * 60));
  }

  postDesignMetoceanData(formData: FormData): Observable<any> {
    return this.http.post<any>(
      environment.API_FEA + 'AssetData/UploadData/DesignMetoceanDataReference', formData
    ).pipe(timeout(1000 * 60 * 60));
  }

    postWorkflow(requestObject){
    return this.http.post(
      environment.API_FEA + 'Workflow/Create', requestObject
    ).pipe(timeout(1000 * 60 * 60));
  }

  createFeaUploadUrl(request: any, fileType: string): Observable<any> {
    return this.http.post<any>(`${environment.API_FEA}AssetData/CreateUploadURL/${fileType}`, request)
      .pipe(
        timeout(1000 * 60 * 5),
        catchError(error => {
          console.error('Error creating upload URL', error);
          throw error;
        })
      );
  }

  validateFeaUpload(id: string, assetId: string): Observable<any> {
    return this.http.post<any>(`${environment.API_FEA}AssetData/ValidateUpload/${id}/${assetId}`, null)
      .pipe(
        timeout(1000 * 60 * 2),
        catchError(error => {
          console.error('Error validating upload', error);
          throw error;
        })
      );
  }

  validateFeaUploadInDirectory(id: string, assetId: string, fileName: string): Observable<any> {
    return this.http.post<any>(`${environment.API_FEA}AssetData/ValidateUploadInDirectory/${id}/${assetId}/${fileName}`, null)
      .pipe(
        timeout(1000 * 60 * 2),
        catchError(error => {
          console.error('Error validating upload', error);
          throw error;
        })
      );
  }

  addSubmodelData(request: any): Observable<string> {
    return this.http.post(
      `${environment.API_FEA}SubmodelData/AddSubmodelData`,
      request,
      { responseType: 'text' }
    ).pipe(timeout(1000 * 60 * 60));
  }

  // Use this method for direct uploads of files that are always smaller than 100 MB.
  async uploadFileToAzure(file: File, uploadUrl: string): Promise<HttpResponse<any>> {
    const request = new HttpRequest('PUT', uploadUrl, file, {
      headers: new HttpHeaders({
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': 'application/octet-stream'
      }),
      reportProgress: true,
    });

    return lastValueFrom(this.http.request<HttpResponse<any>>(request).pipe(
      timeout(1000 * 60 * 15),
      filter(event => event instanceof HttpResponse),
      map(event => event as HttpResponse<any>)
    ));
  }

  // For files that can exceed 100 MB—such as neutral files, Nastran dat files, Aqwa lis files, etc.—
  // it's recommended to upload in chunks.
  async uploadFileToAzureInChunks(file: File, uploadUrl: string): Promise<HttpResponse<any>> {
    const chunkSize = 50 * 1024 * 1024; // 50MB chunks
    const numChunks = Math.ceil(file.size / chunkSize);
    const blockIds: string[] = [];
    const maxConcurrency = 4;

    // Function to create a chunk upload promise
    const uploadChunk = async (chunk: Blob, blockId: string) => {
      const headers = new HttpHeaders({
        'x-ms-blob-type': 'BlockBlob',
        'x-ms-version': '2020-04-08',
        'Content-Type': 'application/octet-stream'
      });

      await lastValueFrom(this.http.put(`${uploadUrl}&comp=block&blockid=${blockId}`, chunk, { headers }).pipe(
        timeout(1000 * 60 * 5)));
    };

    // Function to generate and upload each chunk
    const chunkPromises = Array.from({ length: numChunks }, (_, i) => {
      const chunk = file.slice(i * chunkSize, (i + 1) * chunkSize);
      const blockId = window.btoa(`block${i}`).trim().replace(/=+$/, '').padEnd(24, "A").substring(0, 24);
      blockIds.push(blockId);
      return () => uploadChunk(chunk, blockId); // Return function to defer execution
    });

    // Process chunks with limited concurrency
    const processChunksWithConcurrency = async (tasks: (() => Promise<void>)[], concurrency: number) => {
      const executing: Promise<void>[] = [];
      for (const task of tasks) {
        const promise = task().finally(() => executing.splice(executing.indexOf(promise), 1));
        executing.push(promise);
        if (executing.length >= concurrency) {
          await Promise.race(executing); // Wait for at least one to finish
        }
      }
      await Promise.all(executing); // Wait for all to complete
    };

    await processChunksWithConcurrency(chunkPromises, maxConcurrency);

    // Commit the block list and capture the response
    const blockListXml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockIds.map(id => `<Latest>${id}</Latest>`).join('')}</BlockList>`;
    const commitResponse = await lastValueFrom(this.http.put(`${uploadUrl}&comp=blocklist`, blockListXml, {
      headers: new HttpHeaders({
        'Content-Type': 'application/xml'
      }),
      observe: 'response' // Ensures we get the full response including status
    }));

    return commitResponse;
  }

  createTemporarySpreadsheetUploadUrl(): Observable<string> {
    return this.http.get(
      `${environment.API_INSPEC}Spreadsheet/CreateTemporaryReportUploadUrl`,
      { responseType: 'text' }
    )
      .pipe(
        timeout(1000 * 60 * 5),
        catchError(error => {
          console.error('Error creating upload URL', error);
          throw error;
        })
      );
  }
}
